import { useEffect, useState } from "react";
import { Space, Table, Tooltip, Popconfirm, Button, Modal, Skeleton, Spin, Typography, Card, Divider, Tag } from "antd";
import { EditOutlined, DeleteOutlined, FilePdfOutlined, EyeOutlined, UserOutlined, FileSearchOutlined, ClockCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import BaseService from "../../service/base.service";
import Examinations from "../../models/examination";
import PaginationResponse from "../../models/paginationResponse";
import PatientProfileCard from "./components/PatientProfileCard";
import ExaminationHeader from "./components/ExaminationHeader";
import ObstetricExaminationModal from "./components/obstetric/ObstetricExaminationModal";
import GynecologicExaminationModal from "./components/gynecologic/GynecologicExaminationModal";
import MedicalConsultationModal from "./components/medical/MedicalConsultationModal";
import notify from "../../components/common/notificationHelper";
import { useIsMobile } from "../../hooks/useIsMobile";

const { Title, Text } = Typography;

type ModalType = "gynecologic" | "obstetric" | "medical" | null;

const ExaminationsPage = () => {
  const isMobile = useIsMobile();
  const { patientId } = useParams<{ patientId: string }>();
  const [modalType, setModalType] = useState<ModalType>(null);
  const [listExaminations, setListExaminations] = useState<Examinations[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState<number | null>(null);
  const [selectedExaminationId, setSelectedExaminationId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("gynecologic");
  const [lastUpdated, setLastUpdated] = useState<string>("—");

  useEffect(() => {
    handleGetExaminations(currentPage, pageSize, selectedType);
  }, [currentPage, pageSize, selectedType]);

  const handleGetExaminations = async (page: number, size: number, type: string) => {
    try {
      setLoading(true);
      let url = "";
      if (type === "gynecologic") {
        url = `/gynecologic-examinations/${patientId}?page=${page}&size=${size}`;
      } else if (type === "obstetric") {
        url = `/obstetric-examinations/${patientId}?page=${page}&size=${size}`;
      } else if (type === "medical") {
        url = `/medical-consultations/${patientId}?page=${page}&size=${size}`;
      }
      const rp = await BaseService.get<PaginationResponse<Examinations>>(url, null);
      if (rp.Status) {
        const paginationResponse = rp.Data || [];
        const examinations = paginationResponse.data.map(
          (p: any) => new Examinations(p.id, p.details, p.examinationDate)
        );
        setListExaminations(examinations);
        setTotalElements(paginationResponse.totalElements);
        setCurrentPage(page);
        setLastUpdated("N/A");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExamination = (id: any, type: string) => {
    let url = "";
    if (type === "gynecologic")
      url = `/gynecologic-examinations/${id}/patients/${patientId}`;
    else if (type === "obstetric")
      url = `/obstetric-examinations/${id}/patients/${patientId}`;
    else if (type === "medical")
      url = `/medical-consultations/${id}/patients/${patientId}`;
    BaseService.delete(url, null).then((rp) => {
      if (rp.Status) {
        notify({
          title: "Success",
          description: `Examination deleted successfully.`,
          variant: "success",
        });
        handleGetExaminations(currentPage, pageSize, selectedType);
        refreshProfileCard();
      } else {
        notify({
          title: "Error",
          description: `Failed to delete examination.`,
          variant: "error",
        });
      }
    });
  };

  const handleAddExamination = (type: ModalType) => {
    setModalType(type);
    setIsEdit(false);
    setSelectedExaminationId(null);
    setIsModalOpen(true);
  };

  const handleEditExamination = (type: ModalType, examination: Examinations) => {
    setModalType(type);
    setIsEdit(true);
    setSelectedExaminationId(examination.id);
    setIsModalOpen(true);
  };

  const handleCancelExamination = () => {
    setIsModalOpen(false);
    setSelectedExaminationId(null);
  };

  const handleDownloadPdf = async (id: number, type: string) => {
    setDownloadLoading(id);
    try {
      let url = "";
      if (type === "gynecologic")
        url = `/gynecologic-examinations/${id}/patients/${patientId}/pdf/download`;
      else if (type === "obstetric")
        url = `/obstetric-examinations/${id}/patients/${patientId}/pdf/download`;
      else if (type === "medical")
        url = `/medical-consultations/${id}/patients/${patientId}/pdf/download`;

      await BaseService.downloadPdf(url);
    } catch {
      notify({
        title: "Error",
        description: "Failed to download PDF.",
        variant: "error",
      });
    } finally {
      setDownloadLoading(null);
    }
  };

  const handlePreviewPdf = async (id: number, type: string) => {
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      let url = "";
      if (type === "gynecologic")
        url = `/gynecologic-examinations/${id}/patients/${patientId}/pdf/preview`;
      else if (type === "obstetric")
        url = `/obstetric-examinations/${id}/patients/${patientId}/pdf/preview`;
      else if (type === "medical")
        url = `/medical-consultations/${id}/patients/${patientId}/pdf/download`;

      const response = await BaseService.previewPdf(url);
      if (response) setTimeout(() => setPreviewUrl(response), 300);
    } catch {
      console.error("Preview PDF failed");
    } finally {
      setTimeout(() => setPreviewLoading(false), 400);
    }
  };

  const refreshProfileCard = () => setRefreshKey((prev) => prev + 1);

  const renderActions = (record: Examinations) => (
    <Space size="small">
      <Tooltip title="Preview PDF">
        <Button
          type="text"
          icon={<EyeOutlined style={{ color: "#52c41a" }} />}
          onClick={() => handlePreviewPdf(record.id, selectedType)}
        />
      </Tooltip>
      <Tooltip title="Download PDF">
        <Button
          type="text"
          icon={<FilePdfOutlined style={{ color: "#fa8c16" }} />}
          onClick={() => handleDownloadPdf(record.id, selectedType)}
        />
      </Tooltip>
      <Tooltip title="Edit Examination">
        <Button
          type="text"
          icon={<EditOutlined style={{ color: "#1677ff" }} />}
          onClick={() => handleEditExamination(selectedType as ModalType, record)}
        />
      </Tooltip>
      <Popconfirm
        title="Are you sure you want to delete this examination?"
        onConfirm={() => handleDeleteExamination(record.id, selectedType)}
        okText="Yes"
        cancelText="No"
        okButtonProps={{ danger: true }}
      >
        <Tooltip title="Delete Examination">
          <Button type="text" icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />} />
        </Tooltip>
      </Popconfirm>
    </Space>
  );

  const columns = isMobile
    ? [
        {
          title: "Examination",
          key: "exam",
          render: (_: unknown, record: Examinations) => (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: "#1677ff" }}>#{record.id}</div>
              <div style={{ fontSize: 13, color: "#1f2937", marginTop: 2 }}>
                {record.details || "—"}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {record.examinationDate}
              </div>
            </div>
          ),
        },
        {
          title: "",
          key: "action",
          width: 148,
          align: "right" as const,
          render: (_: unknown, record: Examinations) => renderActions(record),
        },
      ]
    : [
        {
          title: "Examination ID",
          dataIndex: "id",
          render: (text: string) => <a style={{ color: "#1677ff" }}>{text}</a>,
        },
        {
          title: "Details",
          dataIndex: "details",
          render: (text: string) => text || <Text type="secondary">—</Text>,
        },
        {
          title: "Examination Date",
          dataIndex: "examinationDate",
        },
        {
          title: "Action",
          render: (_: unknown, record: Examinations) => renderActions(record),
        },
      ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
        height: "100%",
        padding: isMobile ? "8px 0 0" : "16px 16px 0",
        boxSizing: "border-box",
      }}
      className="modern-scroll"
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: isMobile ? 24 : 80,
          scrollBehavior: "smooth",
          paddingRight: isMobile ? 0 : 12,
          marginRight: isMobile ? 0 : -12,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <AnimatePresence>
          {downloadLoading && (
            <motion.div
              key="download-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "#fff",
              }}
            >
              <Spin size="large" />
              <div style={{ marginTop: 16, fontSize: 18 }}>Generating PDF…</div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Patient Section */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: isMobile ? 12 : 16 }}>
          <UserOutlined style={{ fontSize: isMobile ? 18 : 22, color: "#1677ff" }} />
          <Title level={3} style={{ margin: 0, color: "#1f1f1f", fontSize: isMobile ? 18 : undefined }}>
            Patient Profile
          </Title>
        </div>

        {patientId ? (
          <PatientProfileCard key={refreshKey} patientId={patientId} />
        ) : (
          <Text type="danger">Invalid or missing patient ID</Text>
        )}

        <Divider style={{ margin: isMobile ? "24px 0 16px 0" : "40px 0 24px 0" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <FileSearchOutlined style={{ fontSize: isMobile ? 18 : 22, color: "#1677ff" }} />
          <Title level={3} style={{ margin: 0, color: "#1f1f1f", fontSize: isMobile ? 18 : undefined }}>
            Examinations
          </Title>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedType + totalElements + lastUpdated} // ensures animation triggers on change
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{
              background: "#fafafa",
              borderRadius: 10,
              padding: isMobile ? "10px 12px" : "10px 16px",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: isMobile ? 8 : 0,
              marginBottom: 16,
              boxShadow: "inset 0 0 4px rgba(0,0,0,0.05)",
            }}
          >
            <Space size="middle">
              <Tag color="blue">
                <FileTextOutlined />{" "}
                {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Type
              </Tag>
              <Tag color="geekblue">
                Total: {totalElements ?? 0}
              </Tag>
            </Space>
            <Space size="small">
              <ClockCircleOutlined style={{ color: "#1677ff" }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Last Updated: {lastUpdated}
              </Text>
            </Space>
          </motion.div>
        </AnimatePresence>
        <Card
          style={{
            borderRadius: isMobile ? 12 : 16,
            padding: isMobile ? 12 : 24,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            background: "#fff",
          }}
        >
          <ExaminationHeader
            selectedType={selectedType}
            onTypeChange={(value) => {
              setSelectedType(value);
              setCurrentPage(0);
            }}
            handleAddExamination={handleAddExamination}
          />
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedType + totalElements} // triggers animation on type or data change
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <Table
                className="fixed-table-body"
                columns={columns}
                dataSource={listExaminations}
                rowKey="id"
                loading={loading}
                pagination={{
                  position: ["bottomCenter"],
                  current: currentPage + 1,
                  total: totalElements,
                  pageSize,
                  showSizeChanger: !isMobile,
                  simple: isMobile,
                  size: isMobile ? "small" : "default",
                  pageSizeOptions: ["5", "10", "20", "50"],
                  onChange: (page) => setCurrentPage(page - 1),
                  onShowSizeChange: (_, size) => {
                    setPageSize(size);
                    setCurrentPage(0);
                  },
                }}
                locale={{ emptyText: "No examinations found" }}
                scroll={{ y: isMobile ? 280 : 320, x: isMobile ? undefined : true }}
                size={isMobile ? "small" : "middle"}
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              />
            </motion.div>
          </AnimatePresence>
          {modalType === "gynecologic" && (
            <GynecologicExaminationModal
              isEdit={!!selectedExaminationId}
              isModalOpen={isModalOpen}
              onCancel={handleCancelExamination}
              patientId={patientId!}
              examinationId={selectedExaminationId}
              onSuccess={() => {
                handleGetExaminations(currentPage, pageSize, selectedType);
                refreshProfileCard();
              }}
            />
          )}
          {modalType === "obstetric" && (
            <ObstetricExaminationModal
              isEdit={!!selectedExaminationId}
              isModalOpen={isModalOpen}
              onCancel={handleCancelExamination}
              patientId={patientId!}
              examinationId={selectedExaminationId}
              onSuccess={() => {
                handleGetExaminations(currentPage, pageSize, selectedType);
                refreshProfileCard();
              }}
            />
          )}
          {modalType === "medical" && (
            <MedicalConsultationModal
              isEdit={!!selectedExaminationId}
              isModalOpen={isModalOpen}
              onCancel={handleCancelExamination}
              patientId={patientId!}
              medicalConsultationId={selectedExaminationId}
              onSuccess={() => {
                handleGetExaminations(currentPage, pageSize, selectedType);
                refreshProfileCard();
              }}
            />
          )}
          <Modal
            open={previewLoading || !!previewUrl}
            onCancel={() => {
              if (previewUrl) {
                try { URL.revokeObjectURL(previewUrl); } catch { }
              }
              setPreviewUrl(null);
              setPreviewLoading(false);
            }}
            footer={null}
            width={isMobile ? "calc(100vw - 16px)" : "90%"}
            style={{ top: isMobile ? 8 : 20 }}
            styles={{
              body: {
                height: isMobile ? "80vh" : "85vh",
                padding: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}
            zIndex={1500}
            destroyOnClose
            closeIcon={
              <div
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 'bold',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)',
                  cursor: 'pointer',
                }}
              >
                ✕
              </div>
            }
          >
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {previewLoading && (
                <div style={{ width: '80%', textAlign: 'center' }}>
                  <Skeleton active paragraph={{ rows: 6 }} title={{ width: '40%' }} />
                  <div style={{ color: '#aaa', marginTop: 12 }}>Preparing preview…</div>
                </div>
              )}

              {!previewLoading && previewUrl && (
                <object
                  data={previewUrl}
                  type="application/pdf"
                  aria-label="PDF preview"
                  style={{ width: '100%', height: '100%', minHeight: '70vh', background: '#111' }}
                >
                  <iframe
                    src={previewUrl}
                    title="pdf preview fallback"
                    style={{ width: '100%', height: '100%', border: 'none', background: '#111' }}
                  />
                </object>
              )}
            </div>
          </Modal>
        </Card>
      </div>
    </div>
  );
};

export default ExaminationsPage;
